import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Provider {
  id: number;
  name: string;
  email: string;
  role: string;
  permissions: {
    view: boolean;
    edit: boolean;
    schedule: boolean;
    billing: boolean;
  };
}

interface Office {
  id: number;
  name: string;
  subdomain: string;
  subscription_tier: string;
}

interface AuthContextType {
  token: string | null;
  provider: Provider | null;
  office: Office | null;
  isAuthenticated: boolean;
  isDemo: boolean;
  isLoading: boolean;
  login: (email: string, password: string, subdomain: string) => Promise<void>;
  loginDemo: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [office, setOffice] = useState<Office | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on mount
    const storedToken = localStorage.getItem('auth_token');
    const storedProvider = localStorage.getItem('provider');
    const storedOffice = localStorage.getItem('office');
    const storedIsDemo = localStorage.getItem('is_demo') === 'true';

    if (storedToken && storedProvider && storedOffice) {
      setToken(storedToken);
      setProvider(JSON.parse(storedProvider));
      setOffice(JSON.parse(storedOffice));
      setIsDemo(storedIsDemo);
    }

    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string, subdomain: string) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, office_subdomain: subdomain }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Innskráning mistókst' })) as { error?: string };
        throw new Error(error.error || 'Innskráning mistókst');
      }

      const data = await response.json() as { token?: string; provider?: Provider; office?: Office };

      // Validate response data
      if (!data.token || !data.provider || !data.office) {
        throw new Error('Ógild svörun frá þjóni');
      }

      setToken(data.token);
      setProvider(data.provider);
      setOffice(data.office);
      setIsDemo(false);

      // Persist to localStorage
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('provider', JSON.stringify(data.provider));
      localStorage.setItem('office', JSON.stringify(data.office));
      localStorage.setItem('is_demo', 'false');
    } catch (error) {
      // Re-throw the error to be handled by the login form
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Innskráning mistókst');
    }
  };

  const loginDemo = async () => {
    const response = await fetch('/api/auth/demo', { method: 'POST' });

    if (!response.ok) {
      throw new Error('Demo innskráning mistókst');
    }

    const data = await response.json() as {
      token?: string;
      provider?: Provider;
      office?: Office;
      is_demo?: boolean;
    };

    if (!data.token || !data.provider || !data.office) {
      throw new Error('Ógild svörun frá þjóni');
    }

    setToken(data.token);
    setProvider(data.provider);
    setOffice(data.office);
    setIsDemo(true);

    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('provider', JSON.stringify(data.provider));
    localStorage.setItem('office', JSON.stringify(data.office));
    localStorage.setItem('is_demo', 'true');
  };

  const logout = () => {
    setToken(null);
    setProvider(null);
    setOffice(null);
    setIsDemo(false);

    localStorage.removeItem('auth_token');
    localStorage.removeItem('provider');
    localStorage.removeItem('office');
    localStorage.removeItem('is_demo');
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        provider,
        office,
        isAuthenticated: !!token,
        isDemo,
        isLoading,
        login,
        loginDemo,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
